/*
 * Helpers to create events for specific context.
 */

import type * as api from '@gitbook/api-client';
import type {
    ChangeRequest,
    Collection,
    Comment,
    ContentOwnership,
    Space,
} from '@gitbook/app-database';
import {
    getNotificationContext,
    type MagicBellNotification,
    type NotificationMetadata,
} from '@gitbook/app-notifications';
import type * as hiveCore from '@gitbook/hive-core';
import { assertNever } from '@gitbook/util-ts';

import type {
    AnalyticsContentMoveEvent,
    AnalyticsContextChangeRequest,
    AnalyticsContextCollection,
    AnalyticsContextComment,
    AnalyticsContextNotification,
    AnalyticsContextOrg,
    AnalyticsContextPage,
    AnalyticsContextSite,
    AnalyticsContextSiteFinding,
    AnalyticsContextSpace,
    GitSyncSetupAnalyticsEvent,
    GitSyncSetupEventPayload,
    GitSyncSetupScope,
    RawAnalyticsEventContext,
} from './types';

/**
 * Extract the context of an event.
 */
export function extractEventContext<T extends Partial<RawAnalyticsEventContext>>(
    event: T
): { context: RawAnalyticsEventContext; properties: Omit<T, keyof RawAnalyticsEventContext> } {
    const {
        organization,
        site,
        space,
        spaceType,
        collection,
        changeRequest,
        integration,
        team,
        page,
        comment,
        finding,
        screen,
        screenSection,
        screenTab,
        ...properties
    } = event;
    const context: RawAnalyticsEventContext = {
        organization,
        site,
        space,
        spaceType,
        collection,
        changeRequest,
        team,
        page,
        finding,
        screen,
        screenSection,
        screenTab,
        integration,
    };

    // Filter out non string
    Object.keys(context).forEach((key) => {
        const value = context[key];
        if (!value) {
            delete context[key];
        }
    });

    return { context, properties };
}

/**
 * Create an event for an organization.
 */
export function createOrgEvent<T>(legacyOrgKey: string, event: T): T & AnalyticsContextOrg {
    return { ...event, organization: legacyOrgKey };
}

/**
 * Create an event for a site.
 */
export function createSiteEvent<T>(
    site: Pick<api.Site, 'id'> & { organization?: string | undefined },
    event: T
): T & AnalyticsContextSite {
    return {
        ...event,
        site: site.id,
        organization: site.organization,
    };
}

/**
 * Create an event for a site finding.
 */
export function createSiteFindingEvent<T>(
    site: Pick<api.Site, 'id'> & { organization?: string | undefined },
    finding: string,
    event: T
): T & AnalyticsContextSite & AnalyticsContextSiteFinding {
    return createSiteEvent(site, { ...event, finding });
}

/**
 * Create an event for a space.
 */
export function createSpaceEvent<T>(
    space:
        | (Pick<Space, 'key' | 'collection' | 'type'> & { organization?: string | undefined })
        | Pick<api.Space, 'id' | 'organization' | 'parent' | 'type'>,
    event: T
): T & AnalyticsContextSpace & Partial<AnalyticsContextCollection> {
    // Tag `styleguide_*` events so we can tell style guide activity apart from
    // regular space activity; regular spaces omit `spaceType`.
    const spaceType = space.type === 'styleguide' ? ('styleguide' as const) : undefined;

    if ('id' in space) {
        // When it's an api.Space
        return {
            ...event,
            space: space.id,
            organization: space.organization,
            collection: space.parent,
            spaceType,
        };
    }

    return {
        ...event,
        space: space.key,
        organization: space.organization,
        collection: space.collection,
        spaceType,
    };
}

/**
 * Create a Git Sync setup event, naming it after its scope (e.g. `site_gitsync_auth_start`).
 * Wrap the result with `createSiteEvent` or `createSpaceEvent` to attach the target.
 */
export function createGitSyncSetupEvent<Scope extends GitSyncSetupScope>(
    scope: Scope,
    payload: GitSyncSetupEventPayload
): GitSyncSetupAnalyticsEvent<Scope> {
    const { name, ...properties } = payload;
    return {
        ...properties,
        type: `${scope}_gitsync_${name}`,
    } as unknown as GitSyncSetupAnalyticsEvent<Scope>;
}

/**
 * A convenience type for any function passing a Space-like object to createSpaceEvent.
 */
type AnalyticsSpace = Parameters<typeof createSpaceEvent>[0];

/**
 * Create an event for a page.
 */
export function createPageEvent<T>(
    space: AnalyticsSpace,
    page: hiveCore.RevisionPage,
    event: T
): T & ReturnType<typeof createSpaceEvent> & AnalyticsContextPage {
    return createSpaceEvent(space, {
        ...event,
        page: page.id,
    });
}

/**
 * Create an event for a collection.
 */
export function createCollectionEvent<T>(
    collection: (Pick<Collection, 'key'> & { organization?: string | undefined }) | api.Collection,
    event: T
): T & AnalyticsContextCollection & Partial<AnalyticsContextCollection> {
    if ('id' in collection) {
        // When it's an api.Collection
        return {
            ...event,
            collection: collection.id,
            organization: collection.organization,
        };
    }

    return {
        ...event,
        organization: collection.organization,
        collection: collection.key,
    };
}

/**
 * Create an event for change request.
 */
export function createChangeRequestEvent<T>(
    space: AnalyticsSpace,
    changeRequest: ChangeRequest | string,
    event: T
): T & ReturnType<typeof createSpaceEvent> & AnalyticsContextChangeRequest {
    return createSpaceEvent(space, {
        ...event,
        changeRequest: typeof changeRequest === 'string' ? changeRequest : changeRequest.key,
    });
}

/**
 * Create an event for an edit operation.
 */
export function createEditEvent<T>(
    space: AnalyticsSpace,
    changeRequestKey: string | undefined,
    event: T
): T & ReturnType<typeof createSpaceEvent> & Partial<AnalyticsContextChangeRequest> {
    return createSpaceEvent(space, {
        ...event,
        space: 'id' in space ? space.id : space.key,
        changeRequest: changeRequestKey,
    });
}

/**
 * Create an event for a comment.
 */
export function createCommentEvent<T>(
    comment: Comment | api.Comment | string,
    event: T
): T & AnalyticsContextComment {
    return {
        ...event,
        comment:
            typeof comment === 'string' ? comment : 'key' in comment ? comment.key : comment.id,
    };
}

/**
 * Create a tracking event for moving content from one owner to another.
 */
export function createContentMoveEvent<T>(
    origin: ContentOwnership,
    destination: ContentOwnership,
    action: 'drag-drop' | 'palette',
    event: T
): T & AnalyticsContentMoveEvent {
    // Which scope we're moving from
    const fromScopeKey = origin.organization;

    // Which scope we're moving to
    const toScopeKey = destination.organization;

    return {
        action,

        // Track which scope we're moving to/from
        fromScopeKey,
        fromScopeType: 'org',
        toScopeType: 'org',
        toScopeKey,

        // Track the container we're moving from
        ...('collection' in origin && origin.collection
            ? {
                  fromContainerKey: origin.collection,
                  fromContainerType: 'collection',
              }
            : {
                  fromContainerKey: fromScopeKey,
                  fromContainerType: 'root',
              }),

        // Track the container we're moving to
        ...('collection' in destination && destination.collection
            ? {
                  toContainerKey: destination.collection,
                  toContainerType: 'collection',
              }
            : {
                  toContainerKey: toScopeKey,
                  toContainerType: 'root',
              }),

        ...event,
    };
}

/**
 * Creates the analytics context for a remote notification.
 */
export function createNotificationEvent<T>(
    notification: MagicBellNotification,
    eventWithoutContext: T
): T &
    AnalyticsContextNotification &
    Partial<AnalyticsContextSpace> &
    Partial<AnalyticsContextCollection> {
    const metadata = notification.customAttributes as NotificationMetadata;
    const context = getNotificationContext({
        topic: notification.topic,
        category: notification.category,
    });

    const event = {
        ...(context || {}),
        ...eventWithoutContext,
        notification: notification.id,
        isRead: notification.isRead,
    };

    switch (metadata.type) {
        case 'change_request_review_requested':
        case 'change_request_reviewed':
        case 'review_comment_posted':
            return createChangeRequestEvent(
                {
                    key: metadata.space,
                    organization: metadata.organization,
                },
                metadata.changeRequest,
                event
            );
        case 'comment_mention':
        case 'comment_posted':
        case 'comment_reply':
            return createCommentEvent(
                metadata.comment,
                createSpaceEvent(
                    {
                        key: metadata.space,
                        organization: metadata.organization,
                        collection: metadata.collection,
                    },
                    event
                )
            );
        case 'custom_domain_status_updated':
        case 'ai_features_paused':
        case 'low_ai_credit_balance':
        case 'organization_member_removal_requested':
        case 'upgrade_requested':
            return event;
        default:
            assertNever(metadata);
    }

    return event;
}
